// SwipeAction — wrap any row with swipe-left = reject, swipe-right = approve.
// Uses framer-motion drag. Reveals color-coded action layers behind the row.
// Threshold: 80px in either direction commits the action; below that snaps back.
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Check, X } from "lucide-react";

const THRESHOLD = 80;

export default function SwipeAction({ children, onApprove, onReject, disabled, testid }) {
  const x = useMotionValue(0);
  // Fade action icons based on drag distance
  const approveOpacity = useTransform(x, [0, THRESHOLD], [0, 1]);
  const rejectOpacity = useTransform(x, [-THRESHOLD, 0], [1, 0]);
  const approveScale = useTransform(x, [0, THRESHOLD], [0.6, 1]);
  const rejectScale = useTransform(x, [-THRESHOLD, 0], [1, 0.6]);
  // Background hue reveals commit intent
  const bgColor = useTransform(
    x,
    [-THRESHOLD, 0, THRESHOLD],
    ["hsl(0 84% 60%)", "transparent", "hsl(142 76% 45%)"],
  );

  const handleDragEnd = (_e, info) => {
    if (disabled) return;
    if (info.offset.x > THRESHOLD && onApprove) onApprove();
    else if (info.offset.x < -THRESHOLD && onReject) onReject();
  };

  return (
    <div className="relative overflow-hidden rounded-2xl" data-testid={testid}>
      {/* Action background layer */}
      <motion.div
        className="absolute inset-0 flex items-center justify-between px-6 pointer-events-none rounded-2xl"
        style={{ background: bgColor }}
      >
        <motion.div style={{ opacity: approveOpacity, scale: approveScale }} className="text-white flex items-center gap-2 font-semibold text-[13px]">
          <Check size={18} strokeWidth={2.5} />
        </motion.div>
        <motion.div style={{ opacity: rejectOpacity, scale: rejectScale }} className="text-white flex items-center gap-2 font-semibold text-[13px]">
          <X size={18} strokeWidth={2.5} />
        </motion.div>
      </motion.div>

      {/* Draggable content */}
      <motion.div
        drag={disabled ? false : "x"}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.15}
        dragSnapToOrigin
        style={{ x }}
        onDragEnd={handleDragEnd}
        className="relative touch-pan-y"
        whileTap={{ cursor: "grabbing" }}
      >
        {children}
      </motion.div>
    </div>
  );
}
